import Model from '@ember-data/model';
import { reject, resolve, Promise } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { decamelize } from '@ember/string';
import { FACTOID } from 'sh-www/models/factoid';
import { isValueInObject } from 'sh-www/utils/misc';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

import { VET_USER_INCLUDES } from 'sh-www/routes/admin/user/rate';
import { USER } from 'sh-www/models/user';

export const SIGNUP_STEP_VALIDATE_EMAIL = USER.SELLER_SIGNUP_STEPS.VALIDATE_EMAIL;
export const SIGNUP_STEP_EMAIL_PASSWORD = USER.SELLER_SIGNUP_STEPS.EMAIL_PASSWORD;
export const SIGNUP_STEP_BASIC_PROFILE = USER.SELLER_SIGNUP_STEPS.BASIC_PROFILE;
export const SIGNUP_STEP_EXTENDED_PROFILE = USER.SELLER_SIGNUP_STEPS.EXTENDED_PROFILE;
export const SIGNUP_STEP_WORK_SAMPLES = USER.SELLER_SIGNUP_STEPS.WORK_SAMPLES;

/**
 * @param {DS.Model} model
 * @returns {{ id: string, type: string }}
 */
const relationshipData = (model) => ({
  id: model.get('id'),
  type: model.get('constructor.modelName'),
});

/**
 * @param {Object} relationships
 * @returns {Object}
 */
const serializeRelationships = (relationships) => {
  return Object.keys(relationships).reduce((accum, prop) => {
    const model = relationships[prop];
    const decamelizedProp = decamelize(prop);

    if (model instanceof Model) {
      accum[decamelizedProp] = { data: relationshipData(model) };
    } else if (Array.isArray(model)) {
      accum[decamelizedProp] = { data: model.map(relationshipData) };
      // this will catch both null and undefined
    } else if (model == null) {
      accum[decamelizedProp] = null;
    } else {
      throw new Error('UserCommands: undefined relationship type in relationship serializer');
    }

    return accum;
  }, { });
};

/**
 * @param {{ data: * }} response
 * @param {Object} relationships
 * @returns {Object}
 */
const appendRelationshipsToResponse = (response, relationships) => {
  if (!relationships) {
    return response;
  }

  if (!response.data.relationships) {
    response.data.relationships = { };
  }

  Object.assign(response.data.relationships, relationships);
  return response;
};

/**
 * @param {Object} props
 * @returns {Object}
 */
const decamelizeProps = (props) => Object.keys(props).reduce((accum, prop) => {
  accum[decamelize(prop)] = props[prop];
  return accum;
}, { });

/**
 * Service for user related API calls.
 */
export default Service.extend({
  errorTracker: service('error-tracker'),
  session: service(),
  shData: service(),
  api: service(),
  store: service(),
  currentUser: readOnly('session.currentUser'),

  /**
   * Requests a user deletion
   *
   * @param {User} user
   * @param {String} password
   * @return {Ember.RSVP.Promise}
   */
  requestDeletion(user, password) {
    const options = { jsonData: { password } };

    return this.api.post(`/users/${user.id}/request-deletion`, options).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('user', response.data.id);
    });
  },

  /**
   * Declines a user deletion request
   *
   * @param {User} user
   * @return {Promise}
   */
  declineDeletion(user) {
    return this.api.put(
      `admin/users/${user.get('id')}/decline-deletion`,
    );
  },

  /**
   * PERMANENTLY deletes a user
   *
   * @param {User} user
   * @return {Promise}
   */
  delete(user) {
    return this.get('api').delete(
      `users/${user.get('id')}`,
    );
  },

  /**
   * Randomize a user's public slug
   * @param user
   * @returns {Promise}
   */
  randomizePublicSlug(user) {
    return this.api.put(`users/${user.id}/random-public-slug`);
  },

  /**
   * @param {User} user
   * @param {string} fcmToken
   * @param {object} deviceData
   * @param {string} deviceData.deviceID
   * @param {string} deviceData.deviceType
   * @param {string} deviceData.brand
   * @param {string} deviceData.model
   * @param {string} deviceData.platform
   * @param {string} deviceData.osVersion
   * @param {string} deviceData.country
   * @param {string} deviceData.timezone
   * @param {string} deviceData.locale
   * @return {Ember.RSVP.Promise}
   */
  registerDevice(user, fcmToken, deviceData) {
    const userID = user.get('id');
    const endpoint = `users/${userID}/devices`;
    const options = {
      jsonData: {
        fcm_token: fcmToken,
        device_id: deviceData.deviceID,
        device_type: deviceData.deviceType,
        brand: deviceData.brand,
        model: deviceData.model,
        platform: deviceData.platform,
        os_version: deviceData.osVersion,
        country: deviceData.country,
        timezone: deviceData.timezone,
        locale: deviceData.locale,
      },
    };

    return this.get('api').post(endpoint, options);
  },

  /**
   * @param {User} user
   * @param {string} deviceID
   * @return {Ember.RSVP.Promise}
   */
  unregisterDevice(user, deviceID) {
    const userID = user.get('id');
    const endpoint = `users/${userID}/devices/${deviceID}`;
    const options = {};

    return this.get('api').delete(endpoint, options);
  },

  /**
   * Resends verification email to user with given id
   *
   * @param {String} userId
   * @param {String} origin
   * @param {String} email
   * @returns {Ember.RSVP.Promise}
   */
  resendVerifyEmail(userId, origin, opt_email)
  {
    const options = {
      params: {
        origin
      },
    };

    if (origin === 'reset' && opt_email) {
      options.params['newEmail'] = opt_email;
    }

    return this.api.post(`users/${userId}/email/send-verify`, options);
  },

  /**
   * Updates the user's email address
   * @param user
   * @param email
   * @returns {*}
   */
  updateEmail(user, email)
  {
    return this.api.put(`users/${user.id}/update-email`, {
      jsonData: {
        email,
      },
    });
  },

  /**
   * @param {DS.Model} user
   * @param {string} slug
   * @return {Ember.RSVP.Promise}
   */
  updateSlug(user, slug) {
    if (!slug) {
      return reject(new Error('You must provide a valid slug'));
    }

    if (user.get('slug') === slug) {
      return resolve(user);
    }

    const options = {
      jsonData: {
        slug,
      },
    };

    return this.get('api').put(`users/${user.get('id')}/update-slug`, options).then((response) => {
      const store = this.get('store');
      store.pushPayload(response);
      return store.peekRecord('user', response.data.id);
    });
  },

  /**
   * @param {string} userId
   * @param {string} completed_step
   * @returns {Ember.RSVP.Promise}
   */
  async markFunnelStepComplete(userId, completed_step) {
    await this.api.put(`users/${userId}/update-signup-step`, {
      jsonData: {
        completed_step,
      },
    });
  },

  /**
   * @param {DS.Model|string} user
   * @param {Object|undefined} props
   * @param {Object|undefined} relationships
   * @returns {Promise<DS.Model>}
   */
  updateUser(user, props, relationships) {
    const userId = (user instanceof Model) ? user.get('id') : user;

    return this._updateModel(`users/${userId}`, 'user', props, relationships);
  },

  /**
   * @param {DS.Model|string} user
   * @param {DS.Model|string} profile
   * @param {Object|undefined} props
   * @param {Object|undefined} relationships
   * @returns {Promise<DS.Model>}
   */
  updateUserProfile(user, profile, props, relationships) {
    const userId = (user instanceof Model) ? user.get('id') : user;
    const profileId = (profile instanceof Model) ? profile.get('id') : profile;

    return this._updateModel(`users/${userId}/profiles/${profileId}`, 'profile', props, relationships);
  },

  /**
   * @param {string} url
   * @param {user|profile} model
   * @param {Object|undefined} props
   * @param {Object|undefined} relationships
   * @returns {Promise<DS.Model>}
   * @private
   */
  _updateModel(url, model, props, relationships) {
    const jsonData = { };

    const serializedProps = props ? decamelizeProps(props) : undefined;
    const serializedRelationships = relationships ? serializeRelationships(relationships) : undefined;

    if (serializedProps) Object.assign(jsonData, serializedProps);
    if (serializedRelationships) Object.assign(jsonData, { relationships: serializedRelationships });

    return this.get('api').put(url, { jsonData }).then((response) => {
      const store = this.get('store');
      store.pushPayload(appendRelationshipsToResponse(response, serializedRelationships));
      return store.peekRecord(model, response.data.id);
    }).catch((e) => {
      this.get('errorTracker').error(`UserCommands::_updateModel - error updating model type [${model}]`, this, e);
      throw e;
    });
  },

  /**
   * Given the userId, make a call to accept the latest legal terms on their behalf.
   *
   * @param {String} userId
   * @return {Ember.RSVP.Promise}   Resolve true on success and Error on failure.
   */
  acceptLatestLegal(userId) {
    let options = {};

    return new Promise((resolve, reject) => {
      this.get('api').post(`users/${userId}/legals/accept`, options).then(response => {
        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error('acceptLatestLegal -- error accepting legals', this, e);
        reject(e);
      });
    });
  },

  /**
   * Create a new user with the params passed in.
   *
   * @param {Object} params
   * @param {String[]=} opt_includes
   * @param {Boolean=} opt_isHandleError
   * @return {Ember.RSVP.Promise}
   */
  createUser(params, opt_includes = [], opt_isHandleError = true) {
    return new Promise((resolve, reject) => {
      let options = {
        jsonData: params
      };

      this.shData.addIncludesToOptions(options, opt_includes);

      this.get('api').post(`users/`, options).then(response => {
        // This response has all the bearer information.
        resolve(response);
      }).catch((e) => {
        if (opt_isHandleError) {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error creating your account. Please refresh and try again.',
            'createUser -- error creating user. user id: ',
          );
        }

        reject(e);
      });
    });
  },

  /**
   * @param {User} userToBlock
   * @param {String} note
   */
  blockUser(userToBlock, note) {
    let options = {
      params: {
        note: note
      }
    };

    return new Promise((resolve, reject) => {
      this.get('api').post(`users/${userToBlock.get('id')}/block`, options).then((response) => {
        this.shData.pushResponseToStore(response);
        let blockUserEvent = this.get('store').peekRecord('blockUserEvent', response.data.id),
          currentUser = this.get('currentUser');
        blockUserEvent.set('blocker', currentUser.get('company'));
        blockUserEvent.set('blockedByUser', currentUser);
        blockUserEvent.set('user', userToBlock);

        userToBlock.set('isBlocked', true);

        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error('blockUser -- error blocking user id ' + userToBlock.get('id'), this, e);
        reject(e);
      });
    });
  },

  /**
   * Load a user with a given id.
   *
   * @param {String} userIdToLoad
   * @param {String[]=} opt_includes
   */
  getUser(userIdToLoad, opt_includes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').request(`users/${userIdToLoad}`, options).then(response => {
        this.shData.pushResponseToStore(response);

        let user = this.get('store').peekRecord('user', response.data.id);
        resolve(user);
      }).catch(e => {
        this.get('errorTracker').error('getUser -- error loading user id ' + userIdToLoad, this, e);
        reject(e);
      });
    });
  },

  getNetworkUsers(companyId, page = 1, count = 12, filters = {}, includes = []) {
    // Either filter by entire extended network or company network
    const filter = filters.extendedNetwork ? { extendedNetwork: companyId, ...filters } : { network: companyId, ...filters }
    const options = {
      params: {
        page,
        count,
        filter,
        sort: 'full_name',
        sortOrder: 'asc',
      },
    };

    this.shData.addIncludesToOptions(options, includes);

    return this.api.get('users', options);
  },

  /**
   * Get feeStructure for a perspective user + company project.
   *
   * @param {Number} addedLifetimeWorkInCents    Amount the project offer would be in cents
   */
  getHiringFeeStructure(userId, companyId, addedLifetimeWorkInCents = 0) {
    const options = {
      params: {
        added_lifetime_work_in_cents: addedLifetimeWorkInCents,
      }
    };

    return this.api.get(`users/${userId}/companies/${companyId}/get-hiring-fee-structure`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('feeStructure', response.data.id);
    });
  },

  /**
   * Get reviews for the user
   *
   * @param {String} userId
   */
  getUserReviews(userId, count = 3, sort = 'top', opt_includes) {
    const options = {
      params: {
        count,
        sort,
      }
    };

    if (opt_includes) {
      options.params.include = opt_includes;
    }

    return this.api.get(`users/${userId}/reviews`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'Review');
    });
  },

  /**
   * Get action items for a specific user
   *
   * @param {String} userId
   */
  getUserActionItems(userId) {
    return this.api.request(`/users/${userId}/action-items`).then(response => {
      this.shData.pushResponseToStore(response);
      return response.data.map(({ id }) => this.store.peekRecord('user-action-item', id));
    });
  },

  /**
   * Get the user analytics factoids
   *
   * @param {String} userId
   * @param {String} type       of type FACTOID.TYPES
   */
  getUserAnalyticsFactoids(userId, type) {
    if (!isValueInObject(type, FACTOID.TYPES)) {
      this.get('errorTracker').error('getUserAnalyticsFactoids -- invalid type ' + type, this);
      return Promise.reject(false);
    }

    let options = {
      params: {
        type: type
      }
    };

    return new Promise((resolve, reject) => {
      this.get('api').request(`users/${userId}/analytics/factoids`, options).then(response => {
        this.get('store').pushPayload(response);
        resolve(response.data.map(factoidJson => this.get('store').peekRecord('factoid', factoidJson.id)));
      }).catch(e => {
        this.get('errorTracker').error('getUserAnalyticsFactoids -- could not load factoids user id ' + userId + e.toString(), this);
        reject(e);
      });
    });
  },

  /**
   * Get the project analytics for a given user id and Date Range
   *
   * @param {String} userId
   * @param {Date} startAt
   * @param {Date} endAt
   * @returns {Ember.RSVP.Promise}    Resolves with the array of FreelancerProjectMetric models.
   */
  getUserAnalyticsProjects(userId, startAt, endAt) {
    let options = {
      params: {
        start_at: startAt.toISOString(),
        end_at: endAt.toISOString()
      }
    };

    return new Promise((resolve, reject) => {
      this.get('api').request(`users/${userId}/analytics/projects`, options).then(response => {
        this.get('store').pushPayload(response);
        resolve(response.data.map(freelancerProjectMetricJson => this.get('store').peekRecord('freelancerProjectMetric', freelancerProjectMetricJson.id)));
      }).catch(e => {
        this.get('errorTracker').error('getUserAnalyticsProjects -- could not load project analytics user id ' + userId + e.toString(), this);
        reject(e);
      });
    });
  },

  getAnalyticsLTV() {
    return this.get('api').request('analytics/ltv').then((response) => {
      const store = this.get('store');
      store.pushPayload(response);
      return response.data.map(json => store.peekRecord('companyLtvmetric', json.id));
    });
  },

  /**
   * Get the analytics summary for a given user id
   *
   * @param {String} userId
   * @param {String=} opt_companyIdToFilter   Optional company id to filter by
   * @returns {Ember.RSVP.Promise}            Will resolve with the summary model
   */
  getAnalyticsSummary(userId, opt_companyIdToFilter) {
    let options = {
      params: {}
    };

    if (opt_companyIdToFilter) {
      options.params['filter'] = {
        company_id: opt_companyIdToFilter
      };
    }

    let endpointPath = opt_companyIdToFilter ? '/analytics/summary' : `users/${userId}/analytics/summary`;

    return new Promise((resolve, reject) => {
      this.get('api').request(endpointPath, options).then(response => {
        let summary = response.data.reduce((summary, year) => {
          summary[year.attributes.year] = year.attributes.count;

          return summary;
        }, {});

        resolve(summary);
      }).catch(e => {
        this.get('errorTracker').error('getAnalyticsSummary -- could not load analytics summary user id ' + userId + e.toString(), this);
        reject(e);
      });
    });
  },

  /**
   * @param user
   * @returns {*}
   */
  getUserOAuthProviders(user) {
    return this.api.request(`/users/${user.id}/oauth-providers`);
  },

  /**
   * @param user
   * @returns {*}
   */
  getUserSamlProviders(user) {
    return this.api.request(`/users/${user.id}/saml-providers`);
  },

  /**
   * @param {User} userToUnblock
   * @param {String} note
   */
  unblockUser(userToUnblock, note) {
    let options = {
      params: {
        note: note
      }
    };

    return new Promise((resolve, reject) => {
      this.get('api').post(`users/${userToUnblock.get('id')}/unblock`, options).then((response) => {
        this.shData.pushResponseToStore(response);
        let blockUserEvent = this.get('store').peekRecord('blockUserEvent', response.data.id),
          currentUser = this.get('currentUser');
        blockUserEvent.set('blocker', currentUser.get('company'));
        blockUserEvent.set('blockedByUser', currentUser);
        blockUserEvent.set('user', userToUnblock);

        userToUnblock.set('isBlocked', false);

        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error('unblockUser -- error unblocking user id ' + userToUnblock.get('id'), this, e);
        reject(e);
      });
    });
  },

  /**
   * Sets the approved attribute for a user
   *
   * @param {User} user
   * @param {Number} approval
   * @return {Promise}
   */
  setApproval(user, approval) {
    return this.api.put(
      `users/${user.get('id')}/update-approval`,
      {
        params: {
          approval: approval,
        }
      }
    );
  },

  /**
   * Toggle the approval value for a User
   *
   * @param user
   * @return {Promise}
   */
  toggleApproval(user) {
    let newApproval;
    switch (user.approval) {
      case USER.APPROVAL.UN_APPROVED:
      case USER.APPROVAL.UNDEFINED:
        newApproval = USER.APPROVAL.APPROVED;
        break;
      case USER.APPROVAL.APPROVED:
        newApproval = USER.APPROVAL.UN_APPROVED;
        break;
      default:
        throw new Error('Invalid approval value');
    }

    return this.setApproval(user, newApproval);
  },

  /**
   * Sets the Select attribute for a user
   *
   * @param {User} user
   * @param {Boolean} isSelect
   * @return {Promise}
   */
  setSelect(user, isSelect) {
    return this.get('api').put(
      `admin/users/${user.get('id')}/select`,
      {
        params: {
          select: isSelect ? 1 : 0
        }
      }
    );
  },

  /**
   * Sets the rating for a list of services a seller offers
   *
   * @param {User} user
   * @param {Object} serviceRatings object keyed by service id where value is rating
   * @returns {Promise}
   */
  setServiceRatings(user, serviceRatings) {
    return this.api.post(
      `admin/users/${user.id}/review`,
      {
        params: {
          services: serviceRatings,
        }
      }
    );
  },

  /**
   * Sets the approved attribute for a user
   *
   * @param {User} user
   * @param {Boolean} isSuper
   * @return {Promise}
   */
  setSuperAdmin(user, isSuper) {
    return this.get('api').put(
      `users/${user.get('id')}/update-super-admin`,
      {
        params: {
          super_admin: isSuper ? 1 : 0
        }
      }
    );
  },

  /**
   * Sets the approved attribute for a user
   *
   * @param {User} user
   * @param {Number} type
   * @return {Promise}
   */
  setUserType(user, type) {
    return this.get('api').put(
      `users/${user.get('id')}/set-type`,
      {
        params: {
          type: type
        }
      }
    );
  },

  /**
   * Load a projects for a particular user.
   *
   * @param {String} userId
   * @param {Number} opt_page
   * @param {String[]=} opt_includes
   */
  getProjectDigests(userId, opt_includes = [], opt_sort = 'priority', opt_page = 1, opt_count = 10) {
    const options = {
      params: {
        page: opt_page,
        count: opt_count,
        sort: opt_sort,
        filter: {
          user: userId,
        },
      }
    }

    this.shData.addIncludesToOptions(options, opt_includes);

    return this.api.request(`projectdigests/`, options);
  },

  /**
   * Set pinned project for particular user.
   */
  setProjectPinned(projectdigestId, isPinned) {
    const options = {
      params: {
        pinned: isPinned,
      },
    };

    return this.api.post(`projectdigests/${projectdigestId}/set-pinned`, options);
  },

  /**
   * Set a new company for a particular user.
   */
  setUserCompany(userId, companyId, opt_roles=[]) {
    const options = {
      params: {
        company_id: companyId,
      },
    };

    if (opt_roles.length) {
      options.params['roles'] = opt_roles;
    }

    return this.api.post(`users/${userId}/set-company`, options);
  },

  /**
   * Perform a user search with fuzzy matching
   *
   * @param {Object} options
   * @returns {Promise} API call to fetch people by name or email
   */
  searchFreelancerUsers({
    perspectiveCompanyId,
    perspectiveUserId,
    searchName,
    matchIdOrEmail,
    network,
    sort,
    page = 0,
    includes = [],
    filter = {},
  }) {
    const endpoint = 'users/search';
    const options = {
      params: {
        perspectiveCompanyId,
        perspectiveUserId,
        searchName,
        matchIdOrEmail,
        network,
        sort,
        page,
        filter,
      },
    };

    this.shData.addIncludesToOptions(options, includes);

    return this.api.request(endpoint, options);
  },

  getAdminRateQueueUser() {
    const options = {
      params: {
        include: VET_USER_INCLUDES.join(','),
        filter: {
          type: 1,
          vetting: 3,
          notUnapproved: true,
        },
        modelParams: 'applicationMeta',
        sort: 'created_at',
        sortOrder: 'asc',
        page: 1,
        count: 1,
      }
    };

    // get the next user in the queue
    return this.api.request('/users', options).then(response => {
      this.shData.pushResponseToStore(response);

      return this.shData.getModelArrayFromResponse(response, 'user')[0] ?? null;
    });
  },

  /**
   * Add a company role from a user
   *
   * @param {User} user
   * @param {String} role
   * @returns
   */
  addCompanyRole(user, role) {
    const options = {
      params: {
        role,
      },
    };

    return this.api.post(`users/${user.id}/add-company-role`, options);
  },

  /**
   * Add Google SSO to an account
   * @param credential
   * @returns {*}
   */
  addGoogleJwt(user, credential) {
    return this.api.post(`users/${user.id}/addGoogleJwt`, { jsonData: { credential } });
  },

  /**
   * @param oauthProvider
   * @returns {*}
   */
  removeOauthProvider(oauthProvider) {
    return this.api.delete(`oauth-providers/${oauthProvider.id}`);
  },

  /**
   * Remove a company role from a user
   *
   * @param {User} user
   * @param {String} role
   * @returns
   */
  removeCompanyRole(user, role) {
    const options = {
      params: {
        role,
      },
    };

    return this.api.post(`users/${user.id}/remove-company-role`, options);
  },

  /**
   * Remove all company roles from a user and set as deactivated
   *
   * @param {User} user
   * @returns
   */
  deactivateCompanyMembership(user) {
    return this.api.post(`users/${user.id}/deactivate-company-membership`, {});
  },

  /**
   * Reactivate a user and set them as an editor
   *
   * @param {User} user
   * @returns
   */
  reactivateCompanyMembership(user) {
    return this.api.post(`users/${user.id}/reactivate-company-membership`, {});
  },

  removeUserFromVettingQueue(profile) {
    const options = {
      params: {
        decline_forever: true
      },
    };
    return this.api.post(`profile/${profile.id}/set-review-status`, options);
  },

  /**
   * Returns companies whose email address domains match the user's
   *
   * @param {User} user
   */
  getCompanyDomainMatches(user, count = 100) {
    const options = {
      params: {
        count,
      }
    };

    return this.api.get(`users/${user.id}/company-domain-match`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'Company');
    });
  },

  /**
   * Returns step in sign up flow a buyer is on
   *
   * @param {User} user
   * @param {String[]=} opt_includes
   */
  getBuyerSignupStep(user, opt_includes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_includes);

    return this.api.get(`users/${user.id}/buyer-signup-step`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('BuyerSignupStep', response.data.id);
    });
  },

  /**
   * Returns step in sign up flow a buyer is on
   *
   * @param {User} user
   * @param {String[]=} opt_includes
   */
  getSellerVettingQueueState(user) {
    return this.api.get(`users/${user.id}/seller-vetting-queue-state`, {}).then(response => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('SellerVettingQueueState', response.data.id);
    });
  },
});
